import { countries, getEmojiFlag, type TCountryCode } from 'countries-list';

import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';

import { useSiteVisitorCountry } from './useSiteVisitorCountry';

const NO_COUNTRY_TITLE = 'No country';

/**
 * Button to select the visitor country to use for the preview of a site.
 * Only the countries of the site regions are listed, as any other country resolves to no region.
 * Renders nothing when the site declares no region.
 */
export function SiteVisitorCountryButton(
    props: {
        organizationId: string;
        siteId: string;
    } & Partial<ButtonProps>
) {
    const { organizationId, siteId, ...buttonProps } = props;
    const { regions, country, setCountry } = useSiteVisitorCountry({ organizationId, siteId });
    const regionEntries = Object.entries(regions);

    if (regionEntries.length === 0) {
        return null;
    }

    return (
        <PaletteButton
            kind="secondary"
            size="small"
            {...buttonProps}
            label={country ? getCountryLabel(country) : NO_COUNTRY_TITLE}
            trailingIcon={Icon.ChevronDown}
            palette={() =>
                createPalette({
                    search: { placeholder: 'Search countries' },
                    groups: [
                        {
                            key: 'none',
                            commands: [
                                {
                                    key: 'none',
                                    title: NO_COUNTRY_TITLE,
                                    type: PaletteCommandType.RADIO,
                                    selected: !country,
                                    trigger: () => setCountry(null),
                                },
                            ],
                        },
                        ...regionEntries.map(([regionName, group]): PaletteGroupDef => ({
                            key: regionName,
                            title: regionName,
                            commands: (group?.countries ?? []).map((code) => ({
                                key: code,
                                title: getCountryLabel(code),
                                type: PaletteCommandType.RADIO,
                                selected: country === code,
                                trigger: () => setCountry(code),
                            })),
                        })),
                    ],
                })
            }
        />
    );
}

function getCountryLabel(code: string): string {
    const country = countries[code as TCountryCode];
    return country ? `${getEmojiFlag(code as TCountryCode)} ${country.name}` : code;
}
